import { useMemo } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { createColumns } from '../columns';
import { getTableFilters, TableFilter } from '../filterTypes';
import { sortTableHeaders } from '../sortTableHeaders';
import { filterTableHeaders } from '../filterTableHeaders';
import { useDateTimeFormat } from '../../../conversions/useDateFormat';

export interface UseTableColumnsProps {
  shipData: any[] | undefined;
}

/**
 * Custom hook for generating table columns and filters
 * Handles column creation and filter generation
 * Note: Actions column should be added separately to avoid circular dependencies
 */
export const useTableColumns = ({ shipData }: UseTableColumnsProps) => {
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();
  const { columns, filters } = useMemo(() => {
    if (!shipData || shipData.length === 0) {
      return { columns: [], filters: [] };
    }

    // Process headers
    const rawHeaders = Object.keys(shipData[0]);
    const filteredHeaders = filterTableHeaders(rawHeaders);
    const sortedHeaders = sortTableHeaders(filteredHeaders);

    // Create base columns (without actions)
    const baseColumns = createColumns(
      sortedHeaders,
      formatDateTimeWithTimeZone
    );

    // Generate filters
    const tableFilters = getTableFilters(sortedHeaders);

    return {
      columns: baseColumns,
      filters: tableFilters,
    };
  }, [shipData, formatDateTimeWithTimeZone]);

  /**
   * Get processed headers for other hooks to use
   */
  const processedHeaders = useMemo(() => {
    if (!shipData || shipData.length === 0) {
      return [];
    }

    const rawHeaders = Object.keys(shipData[0]);
    const filteredHeaders = filterTableHeaders(rawHeaders);
    return sortTableHeaders(filteredHeaders);
  }, [shipData]);

  /**
   * Get column information for debugging
   */
  const getColumnInfo = useMemo(() => {
    if (!shipData || shipData.length === 0) {
      return {
        totalColumns: 0,
        headerNames: [],
        processedHeaders: [],
      };
    }

    return {
      totalColumns: columns.length,
      headerNames: Object.keys(shipData[0]),
      processedHeaders,
    };
  }, [columns, shipData, processedHeaders]);

  return {
    columns,
    filters,
    processedHeaders,
    getColumnInfo,
  };
};
